import { validateSync } from "class-validator";
import { unsafe_dynamicTranslate } from "@templafy/translation";
import { keys } from "./keys.js";
import { convertValueToClassValue, convertValuesToClass, getMetadata } from "./fieldMetadata.js";
import { addValidationPointerMessages, createValidationPointersFromClassValidator, getValidationPointerMessages, mergeValidationPointers } from "./validationPointers.js";
function translatedMessage(validationMessage) {
    if (null == validationMessage) return;
    if (null == validationMessage.translationKey) return validationMessage.message;
    const translationArguments = validationMessage.arguments || {};
    try {
        return unsafe_dynamicTranslate(validationMessage.translationKey, translationArguments, validationMessage.message);
    } catch (error) {
        return validationMessage.message;
    }
}
function initializeValidationState(validationClass, initialValues, customValidation, initialLoadingValues = false) {
    const values = convertValuesToClass(validationClass, initialValues);
    const serverValidationPointers = {};
    const clientValidationPointers = validateValues(values, customValidation);
    const validationPointers = mergeValidationPointers(serverValidationPointers, clientValidationPointers);
    const fieldsMetadata = getMetadata(validationClass);
    return {
        loadingValues: initialLoadingValues,
        loadedValues: false,
        loadValuesFailed: null,
        loadValuesError: null,
        fieldsMetadata,
        disableSubmit: false,
        validationPointers,
        clientValidationPointers,
        serverValidationPointers,
        hasErrors: keys(validationPointers).length > 0,
        fields: createValidationStateFields(values, validationPointers, fieldsMetadata, ()=>false),
        values: values
    };
}
function validateValues(values, customValidation) {
    const validationErrors = validateSync(values);
    const validationPointers = createValidationPointersFromClassValidator(validationErrors);
    if (null != customValidation) {
        const addValidation = (fieldName, message)=>{
            addValidationPointerMessages(fieldName, [
                {
                    message
                }
            ], validationPointers);
        };
        customValidation(values, addValidation);
    }
    return validationPointers;
}
const createValidationStateFields = (values, validationPointers, fieldsMetadata, isDirty)=>({
        ...keys(values).reduce((memo, fieldName)=>{
            const metadata = fieldsMetadata[fieldName] || {};
            const pointerMessages = getValidationPointerMessages(fieldName, validationPointers);
            return {
                ...memo,
                [fieldName]: {
                    ...metadata,
                    isDirty: isDirty(fieldName),
                    messages: pointerMessages,
                    message: translatedMessage(pointerMessages[0])
                }
            };
        }, {})
    });
function hasDirtyFieldErrors(validationStateFields) {
    const fields = Object.values(validationStateFields);
    for (const field of fields)if (field.isDirty && field.messages.length > 0) return true;
    return false;
}
function createReducer(validationModel, customValidation) {
    return function(state, action) {
        switch(action.type){
            case 'loadValues':
                return {
                    ...state,
                    loadingValues: true,
                    loadedValues: false,
                    loadValuesError: null
                };
            case 'loadedValues':
                {
                    const newValues = convertValuesToClass(validationModel, action.values);
                    const clientValidationPointers = validateValues(newValues, customValidation);
                    const validationPointers = mergeValidationPointers(state.serverValidationPointers, clientValidationPointers);
                    const hasErrors = keys(validationPointers).length > 0;
                    const fields = createValidationStateFields(newValues, validationPointers, state.fieldsMetadata, ()=>false);
                    return {
                        ...state,
                        loadingValues: false,
                        loadedValues: true,
                        loadValuesError: null,
                        fields,
                        values: newValues,
                        hasErrors,
                        clientValidationPointers
                    };
                }
            case 'loadValuesFailed':
                return {
                    ...state,
                    loadingValues: false,
                    loadedValues: false,
                    loadValuesError: action.error
                };
            case 'setValue':
                {
                    const newValues = convertValuesToClass(validationModel, state.values);
                    const { type } = state.fieldsMetadata[action.fieldName];
                    Object.assign(newValues, state.values, {
                        [action.fieldName]: convertValueToClassValue(type, action.value)
                    });
                    const serverValidationPointers = {
                        ...state.serverValidationPointers
                    };
                    delete serverValidationPointers[action.fieldName];
                    const clientValidationPointers = validateValues(newValues, customValidation);
                    const validationPointers = mergeValidationPointers(serverValidationPointers, clientValidationPointers);
                    const hasErrors = keys(validationPointers).length > 0;
                    const fields = createValidationStateFields(newValues, validationPointers, state.fieldsMetadata, (fieldName)=>fieldName === action.fieldName ? true : state.fields[fieldName].isDirty);
                    return {
                        ...state,
                        disableSubmit: hasErrors && hasDirtyFieldErrors(fields),
                        hasErrors,
                        validationPointers,
                        clientValidationPointers,
                        serverValidationPointers,
                        values: newValues,
                        fields
                    };
                }
            case 'submit':
                {
                    const serverValidationPointers = action.serverValidationPointers ?? state.serverValidationPointers;
                    const validationPointers = mergeValidationPointers(serverValidationPointers, state.clientValidationPointers);
                    const hasErrors = keys(validationPointers).length > 0;
                    return {
                        ...state,
                        hasErrors,
                        disableSubmit: hasErrors,
                        validationPointers,
                        serverValidationPointers,
                        fields: createValidationStateFields(state.values, validationPointers, state.fieldsMetadata, ()=>true)
                    };
                }
            default:
                return state;
        }
    };
}
export { createReducer, createValidationStateFields, hasDirtyFieldErrors, initializeValidationState, translatedMessage, validateValues };
